import { DISABLED_PALN_IDS } from '@store/types';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';

import { ContractStatus } from 'chaika-enums';
import type { Optional } from 'chaika-common-types';
import {
    CalculateCostParams,
    CalculateCostResponse,
    EhrPlanData,
    ServicePlanListItemData,
} from 'chaika-frontend-types/frontend-cms';

import { EhrApi } from '@api/EhrApi';
import { store } from '@store';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { assertIsDefined } from '@utils/assertIsDefined';

import { SingleServicePlansKeyParams, ManyServicesPlansKeyParams } from '../../store/ehr/EhrServicePlans';
import { contractService } from '@organization/service/contract/contractService';
import { legalEntityService } from '@organization/service/legalEntity/legalEntityService';
import { legalEntityBranchService } from '@organization/service/legalEntityBranch/legalEntityBranchService';
import { OptimalPlanQuery, OptimalPlanResponse } from 'chaika-frontend-types/frontend-cms';
import { PlanApi } from '@api';
import { EhrPlanDataWithAvailable } from '@plan/types';
import { isPlanAvailableInOrganization } from '@plan/utils/isPlanAvailableInOrganization';

export interface LoadServicePlanParams {
    ehrId: string;
    serviceIds: string[];
    actualOn: string;
    withoutContracts?: boolean;
    onlyAvailable?: boolean;
}

interface GetPlaneAdditionalParams {
    organizationId?: string;
    includeDisabled?: boolean;
}

class EhrsServicePlansService {
    private planKeyOnload = new Set<string>();

    public async getOptimalPlans(params: OptimalPlanQuery): Promise<OptimalPlanResponse> {
        const { ehrsServicePlans } = store;

        if (!ehrsServicePlans.hasOptimalServicePlan(params)) {
            const optimal = await PlanApi.getOptimalPlans(params);
            ehrsServicePlans.setOptimalServicePlan(params, optimal);
        }

        const optimalServicePlan = ehrsServicePlans.getOptimalServicePlan(params);
        assertIsDefined(optimalServicePlan, 'Optimal service plan is loaded above when missing');

        return optimalServicePlan;
    }

    public getCalculatedCosts(ehrId: string, params: CalculateCostParams): CalculateCostResponse[] {
        const { ehrsServicePlans } = store;

        return ehrsServicePlans.getCalculatedCosts(ehrId, params) ?? [];
    }

    public async loadCalculatedCosts(ehrId: string, params: CalculateCostParams): Promise<void> {
        const { ehrsServicePlans } = store;

        if (!ehrsServicePlans.hasCalculatedCosts(ehrId, params)) {
            const costs = await EhrApi.calculateCostForEhr(ehrId, params);
            ehrsServicePlans.setCalculatedCost(ehrId, params, costs);
        }
    }

    public async loadServicePlans({
        ehrId,
        actualOn,
        serviceIds,
        withoutContracts = false,
        onlyAvailable,
    }: LoadServicePlanParams) {
        const { ehrsServicePlans, priceListStore } = store;

        const compositeServiceIds = serviceIds.filter(id => priceListStore.isServiceComposite(id));
        const includedServices = compositeServiceIds.flatMap(
            id => priceListStore.getCompositeServicePriceByServiceId(id).includedServices,
        );
        const extendedIds = serviceIds.concat(includedServices);
        const uniqServiceIds = uniq(
            extendedIds.filter(
                serviceId =>
                    !ehrsServicePlans.isLoaded({ ehrId, serviceId, actualOn }) &&
                    !this.planKeyOnload.has(ehrsServicePlans.makeServicePlanKey({ ehrId, serviceId, actualOn })),
            ),
        );

        if (uniqServiceIds.length > 0) {
            uniqServiceIds.forEach(serviceId => {
                this.planKeyOnload.add(ehrsServicePlans.makeServicePlanKey({ ehrId, serviceId, actualOn }));
            });
            const servicePlans = await EhrApi.getServicePlans(ehrId, {
                onlyAvailable,
                serviceIds: uniqServiceIds,
                actualOn: DateHelper.format(actualOn, DateFormat.DateWithDashSeparator),
            });
            if (!withoutContracts) {
                await this.loadPlansContracts(flatten(servicePlans.map(({ plans }) => plans)));
            }
            ehrsServicePlans.setServicesPlans(ehrId, servicePlans, actualOn);
            this.planKeyOnload.clear();
        }
    }

    public async loadLoadedPlansContrats(): Promise<void> {
        await this.loadPlansContracts(store.ehrsServicePlans.getAllPlans());
    }

    public async storeServicePlans(
        ehrId: string,
        servicePlans: ServicePlanListItemData[],
        actualOn: string,
    ): Promise<void> {
        await this.loadPlansContracts(flatten(servicePlans.map(({ plans }) => plans)));
        store.ehrsServicePlans.setServicesPlans(ehrId, servicePlans, actualOn);
    }

    public getOrganizationEhrServicePlans(
        params: SingleServicePlansKeyParams | ManyServicesPlansKeyParams,
        { organizationId, includeDisabled }: GetPlaneAdditionalParams = {},
    ): Optional<EhrPlanDataWithAvailable[]> {
        const plans = !!organizationId
            ? this.getEhrServicePlansByOrganizationId(params, organizationId)
            : this.getEhrsServicePlansWithoutContract(params);
        return includeDisabled ? plans : plans?.filter(plan => !DISABLED_PALN_IDS.includes(plan.id));
    }

    private getEhrServicePlansByOrganizationId(
        params: SingleServicePlansKeyParams | ManyServicesPlansKeyParams,
        organizationId: string,
    ): Optional<EhrPlanDataWithAvailable[]> {
        const { legalEntityBranchList } = store;
        const legalEntityId = legalEntityBranchList.getActualOnDayLegalEntityId(organizationId, params.actualOn);

        return !!legalEntityId
            ? this.getLegalEntityEhrServicePlans(params, legalEntityId)
            : this.getEhrsServicePlansWithoutContract(params);
    }

    private getEhrsServicePlansWithoutContract(
        params: SingleServicePlansKeyParams | ManyServicesPlansKeyParams,
    ): Optional<EhrPlanDataWithAvailable[]> {
        const activeContractIds = new Set<string>();

        return store.ehrsServicePlans.getServicePlans(params)?.map(plan => ({
            ...plan,
            available: isPlanAvailableInOrganization(plan, activeContractIds),
        }));
    }

    private getLegalEntityEhrServicePlans(
        params: SingleServicePlansKeyParams | ManyServicesPlansKeyParams,
        legalEntityId: string,
    ): Optional<EhrPlanDataWithAvailable[]> {
        const { ehrsServicePlans, contractList } = store;
        const contractIds = new Set(
            contractList.getFilteredValuesIds({ legalEntityId, status: ContractStatus.Active }),
        );
        const plans = ehrsServicePlans.getServicePlans(params);

        return plans?.map(plan => ({
            ...plan,
            available: isPlanAvailableInOrganization(plan, contractIds),
        }));
    }

    private async loadPlansContracts(plans: EhrPlanData[]): Promise<void> {
        const contractIds = compact(uniq(plans.map(({ contractId }) => contractId)));
        const legalEntityIds = compact(uniq(plans.map(({ payerId }) => payerId)));

        await Promise.all([contractService.loadByIds(contractIds), legalEntityService.loadByIds(legalEntityIds)]);

        await legalEntityBranchService.load();
    }
}

export const ehrsServicePlansService = new EhrsServicePlansService();
